"use client";

import { useRef, useState } from "react";
import { devLabel } from "@/lib/dev-labels";

export type BatchItemType = "url" | "file";

export interface BatchItem {
	id: number;
	type: BatchItemType;
	name: string;
	meta: string;
	cost: number;
}

const INITIAL_ITEMS: BatchItem[] = [
	{
		id: 1,
		type: "url",
		name: "youtube.com/watch?v=oVqhJ03qoUY",
		meta: "12:34 · YouTube · 한국어",
		cost: 12,
	},
	{
		id: 2,
		type: "file",
		name: "노을_드라이브_원본.mp4",
		meta: "8:45 · 142 MB · 1920×1080 · 로컬",
		cost: 9,
	},
	{
		id: 3,
		type: "url",
		name: "tiktok.com/@user/video/7300...",
		meta: "0:58 · TikTok · English",
		cost: 1,
	},
	{
		id: 4,
		type: "file",
		name: "interview_4k_2025-05-13.mov",
		meta: "22:18 · 1.2 GB · 3840×2160 · 로컬",
		cost: 22,
	},
	{
		id: 5,
		type: "url",
		name: "vimeo.com/824729183",
		meta: "6:42 · Vimeo · 자동 감지",
		cost: 7,
	},
];

function detectPlatform(url: string): string {
	const v = url.toLowerCase();
	if (v.includes("tiktok")) return "TikTok";
	if (v.includes("vimeo")) return "Vimeo";
	if (v.includes("youtube") || v.includes("youtu.be")) return "YouTube";
	if (v.includes("instagram")) return "Instagram";
	return "Web";
}

export interface BatchModeProps {
	onStartBatch?: (items: BatchItem[]) => void;
}

export function BatchMode({ onStartBatch }: BatchModeProps) {
	const [items, setItems] = useState<BatchItem[]>(INITIAL_ITEMS);
	const [urlInput, setUrlInput] = useState("");
	const [bulkArea, setBulkArea] = useState("");
	const [showBulk, setShowBulk] = useState(false);
	const seqRef = useRef(6);
	const fileInputRef = useRef<HTMLInputElement>(null);

	function nextId() {
		const id = seqRef.current;
		seqRef.current += 1;
		return id;
	}

	function addUrl(rawUrl: string) {
		const v = rawUrl.trim();
		if (!v) return;
		setItems((prev) => [
			...prev,
			{
				id: nextId(),
				type: "url",
				name: v,
				meta: `분석 대기 · ${detectPlatform(v)}`,
				cost: Math.ceil(Math.random() * 15) + 1,
			},
		]);
	}

	function addBulk() {
		const lines = bulkArea
			.split("\n")
			.map((s) => s.trim())
			.filter(Boolean);
		const newItems: BatchItem[] = lines.map((v) => ({
			id: nextId(),
			type: "url",
			name: v,
			meta: `분석 대기 · ${detectPlatform(v)}`,
			cost: Math.ceil(Math.random() * 15) + 1,
		}));
		setItems((prev) => [...prev, ...newItems]);
		setBulkArea("");
		setShowBulk(false);
	}

	function handleFiles(files: FileList | null | undefined) {
		if (!files) return;
		const newItems: BatchItem[] = [];
		for (let i = 0; i < files.length; i++) {
			const f = files[i];
			if (!f) continue;
			const mb = (f.size / (1024 * 1024)).toFixed(0);
			newItems.push({
				id: nextId(),
				type: "file",
				name: f.name,
				meta: `${mb} MB · 로컬`,
				cost: Math.ceil(Math.random() * 20) + 5,
			});
		}
		setItems((prev) => [...prev, ...newItems]);
	}

	function removeItem(id: number) {
		setItems((prev) => prev.filter((i) => i.id !== id));
	}

	function clearAll() {
		setItems([]);
		seqRef.current = 1;
	}

	const urlCount = items.filter((i) => i.type === "url").length;
	const fileCount = items.length - urlCount;
	const totalCost = items.reduce((s, i) => s + i.cost, 0);

	return (
		<div {...devLabel("11-A6")}>
			{/* Input tools */}
			<div
				style={{ display: "flex", gap: 6, marginBottom: 7, flexWrap: "wrap" }}
				{...devLabel("11-A6a")}
			>
				<BatchTool
					primary
					iconHref="globe"
					label="URL 추가"
					onClick={() => {
						/* focus is in the row below */
					}}
				/>
				<BatchTool
					iconHref="upload"
					label="파일 추가 (다중 선택)"
					onClick={() => fileInputRef.current?.click()}
				/>
				<input
					ref={fileInputRef}
					type="file"
					accept="video/mp4,video/quicktime,video/x-matroska,video/webm,video/x-msvideo"
					multiple
					style={{ display: "none" }}
					onChange={(e) => handleFiles(e.target.files)}
				/>
				<BatchTool
					iconHref="batch"
					label="일괄 붙여넣기"
					onClick={() => setShowBulk((v) => !v)}
				/>
				<BatchTool
					iconHref="trash"
					label="전체 삭제"
					onClick={clearAll}
				/>
				<span
					style={{
						marginLeft: "auto",
						fontSize: 9,
						opacity: 0.5,
						fontFamily: "'Geist Mono',monospace",
						alignSelf: "center",
					}}
				>
					최대 50 항목 · URL + 파일 자유 혼합
				</span>
			</div>

			{/* URL input row */}
			<div
				style={{ display: "flex", gap: 6, marginBottom: 7 }}
				{...devLabel("11-A6b")}
			>
				<svg width="13" height="13" viewBox="0 0 24 24" style={{ alignSelf: "center", opacity: 0.6, color: "#4eb3c1" }} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
					<circle cx="12" cy="12" r="10" />
					<line x1="2" y1="12" x2="22" y2="12" />
					<path d="M12 2a15 15 0 0 1 4 10 15 15 0 0 1-4 10 15 15 0 0 1-4-10 15 15 0 0 1 4-10z" />
				</svg>
				<input
					type="url"
					value={urlInput}
					onChange={(e) => setUrlInput(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							addUrl(urlInput);
							setUrlInput("");
						}
					}}
					placeholder="URL 입력 후 Enter (youtube / tiktok / vimeo / mp4 link)"
					style={{
						flex: 1,
						padding: "6px 10px",
						borderRadius: 5,
						background: "rgba(0,0,0,0.3)",
						border: "1px solid rgba(255,255,255,0.06)",
						color: "#dcdcdc",
						font: "inherit",
						fontFamily: "'Geist Mono',monospace",
						fontSize: 11,
						outline: "none",
					}}
					aria-label="Batch URL input"
				/>
				<BatchTool
					primary
					label="+ Enter"
					onClick={() => {
						addUrl(urlInput);
						setUrlInput("");
					}}
				/>
			</div>

			{/* Bulk paste textarea */}
			{showBulk && (
				<div style={{ marginBottom: 7 }} {...devLabel("11-A6c")}>
					<textarea
						value={bulkArea}
						onChange={(e) => setBulkArea(e.target.value)}
						placeholder={`URL 여러 개 한 번에 (한 줄에 하나씩)\nyoutube.com/watch?v=...\nyoutube.com/watch?v=...\nyoutube.com/watch?v=...`}
						style={{
							width: "100%",
							minHeight: 60,
							padding: 9,
							borderRadius: 6,
							background: "rgba(0,0,0,0.3)",
							border: "1px solid rgba(255,255,255,0.06)",
							color: "#dcdcdc",
							fontFamily: "'Geist Mono',monospace",
							fontSize: 10,
							resize: "vertical",
						}}
					/>
					<BatchTool
						primary
						label="일괄 추가"
						iconHref="plus"
						onClick={addBulk}
					/>
				</div>
			)}

			{/* Queue list */}
			<div
				style={{
					maxHeight: "none",
					border: "1px solid rgba(255,255,255,0.06)",
					borderRadius: 6,
					background: "rgba(0,0,0,0.25)",
				}}
				{...devLabel("11-A6d")}
				data-testid="batch-queue"
			>
				{items.length === 0 ? (
					<div
						style={{
							padding: 18,
							textAlign: "center",
							fontSize: 10,
							opacity: 0.5,
						}}
					>
						큐가 비어 있습니다. URL 또는 파일을 추가하세요.
					</div>
				) : (
					items.map((item, idx) => (
						<BatchRow
							key={item.id}
							idx={idx + 1}
							item={item}
							onRemove={() => removeItem(item.id)}
						/>
					))
				)}
			</div>

			{/* Summary + start */}
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					marginTop: 8,
					padding: "7px 11px",
					borderRadius: 6,
					background: "rgba(212,175,55,0.06)",
					border: "1px solid rgba(212,175,55,0.18)",
					fontSize: 10,
				}}
				{...devLabel("11-A6e")}
			>
				<div>
					대상:{" "}
					<strong style={{ color: "#d4af37", fontFamily: "'Geist Mono',monospace" }}>
						{items.length} 항목
					</strong>{" "}
					· 약{" "}
					<strong style={{ color: "#d4af37", fontFamily: "'Geist Mono',monospace" }}>
						{totalCost} C
					</strong>{" "}
					(URL {urlCount} + 파일 {fileCount})
				</div>
				<button
					type="button"
					onClick={() => onStartBatch?.(items)}
					disabled={items.length === 0}
					style={{
						padding: "6px 16px",
						background: items.length > 0 ? "#fff" : "rgba(255,255,255,0.2)",
						color: items.length > 0 ? "#0a0a0a" : "rgba(255,255,255,0.4)",
						borderRadius: 5,
						fontWeight: 700,
						fontSize: 12,
						border: "none",
						cursor: items.length > 0 ? "pointer" : "not-allowed",
					}}
				>
					batch 분석 시작
				</button>
			</div>
			<div
				style={{
					fontSize: 9,
					opacity: 0.5,
					marginTop: 5,
					fontFamily: "'Geist Mono',monospace",
				}}
			>
				동시 분석 1-4 (Helper 설정) · 실패 시 자동 재시도 · 진행률 실시간
			</div>
		</div>
	);
}

interface BatchToolProps {
	label: string;
	primary?: boolean;
	iconHref?: "globe" | "upload" | "batch" | "trash" | "plus";
	onClick?: () => void;
}

function BatchTool({ label, primary, iconHref, onClick }: BatchToolProps) {
	return (
		<button
			type="button"
			onClick={onClick}
			style={{
				padding: "5px 10px",
				borderRadius: 5,
				background: primary ? "rgba(212,175,55,0.12)" : "rgba(255,255,255,0.04)",
				border: primary
					? "1px solid rgba(212,175,55,0.4)"
					: "1px solid rgba(255,255,255,0.08)",
				color: primary ? "#d4af37" : "#dcdcdc",
				fontSize: 10,
				cursor: "pointer",
				display: "inline-flex",
				alignItems: "center",
				gap: 5,
				fontWeight: 600,
			}}
		>
			{iconHref && <BatchIcon name={iconHref} />}
			{label}
		</button>
	);
}

function BatchIcon({ name }: { name: "globe" | "upload" | "batch" | "trash" | "plus" }) {
	const props = {
		width: 11,
		height: 11,
		viewBox: "0 0 24 24",
		fill: "none",
		stroke: "currentColor",
		strokeWidth: 2,
		strokeLinecap: "round" as const,
		strokeLinejoin: "round" as const,
	};
	switch (name) {
		case "globe":
			return (
				<svg {...props}>
					<circle cx="12" cy="12" r="10" />
					<line x1="2" y1="12" x2="22" y2="12" />
					<path d="M12 2a15 15 0 0 1 4 10 15 15 0 0 1-4 10 15 15 0 0 1-4-10 15 15 0 0 1 4-10z" />
				</svg>
			);
		case "upload":
			return (
				<svg {...props}>
					<path d="M16 16l-4-4-4 4" />
					<line x1="12" y1="12" x2="12" y2="21" />
					<path d="M20.4 14.5A6 6 0 0 0 16 7H8a6 6 0 0 0-1.4 11.4" />
				</svg>
			);
		case "batch":
			return (
				<svg {...props}>
					<line x1="3" y1="6" x2="21" y2="6" />
					<line x1="3" y1="12" x2="21" y2="12" />
					<line x1="3" y1="18" x2="21" y2="18" />
				</svg>
			);
		case "trash":
			return (
				<svg {...props}>
					<polyline points="3 6 5 6 21 6" />
					<path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
				</svg>
			);
		case "plus":
			return (
				<svg {...props} strokeWidth={2.5}>
					<line x1="12" y1="5" x2="12" y2="19" />
					<line x1="5" y1="12" x2="19" y2="12" />
				</svg>
			);
	}
}

function BatchRow({
	idx,
	item,
	onRemove,
}: {
	idx: number;
	item: BatchItem;
	onRemove: () => void;
}) {
	const isUrl = item.type === "url";
	return (
		<div
			style={{
				display: "grid",
				gridTemplateColumns: "28px 22px 1fr 70px 24px",
				gap: 7,
				padding: "6px 9px",
				borderBottom: "1px solid rgba(255,255,255,0.04)",
				alignItems: "center",
				fontSize: 11,
			}}
		>
			<div
				style={{
					fontFamily: "'Geist Mono',monospace",
					fontSize: 10,
					opacity: 0.5,
					textAlign: "center",
					fontWeight: 700,
				}}
			>
				{String(idx).padStart(2, "0")}
			</div>
			<div
				style={{
					width: 22,
					height: 22,
					borderRadius: 4,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					background: isUrl ? "rgba(78,179,193,0.15)" : "rgba(212,175,55,0.15)",
					color: isUrl ? "#4eb3c1" : "#d4af37",
				}}
			>
				<BatchIcon name={isUrl ? "globe" : "upload"} />
			</div>
			<div>
				<div
					style={{
						fontSize: 11,
						fontWeight: 500,
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
					}}
				>
					{item.name}
				</div>
				<div
					style={{
						fontSize: 9,
						opacity: 0.5,
						fontFamily: "'Geist Mono',monospace",
					}}
				>
					{item.meta}
				</div>
			</div>
			<div
				style={{
					textAlign: "right",
					fontSize: 10,
					fontFamily: "'Geist Mono',monospace",
					color: "#d4af37",
					fontWeight: 700,
				}}
			>
				{item.cost} C
			</div>
			<button
				type="button"
				onClick={onRemove}
				aria-label="제거"
				style={{
					width: 22,
					height: 22,
					border: "none",
					background: "transparent",
					color: "#a3a3a3",
					cursor: "pointer",
					borderRadius: 3,
					fontSize: 14,
				}}
			>
				×
			</button>
		</div>
	);
}
